import { AssistChatMenu } from "@/components/Assist/AssistChatMenu";
import { useAssistContext } from "@/components/Assist/AssistContext";
import { DeleteChatDialog } from "@/components/Assist/DeleteChatDialog";
import { EditableTitle } from "@/components/EditableText";
import { ExpertSelectionRow } from "@/components/Expert/selection/ExpertSelectionRow";
import { Dots } from "@/components/Indicator/Dots";
import { Page, PageHeader } from "@/components/app-layout";
import { AppTopRightMenu } from "@/components/app-layout/AppTopRightMenu";
import { BrowserPageTitle } from "@/components/app-layout/BrowserPageTitle";
import { PageBody } from "@/components/app-layout/PageBody";
import { ChatFileManagerProvider } from "@/components/file/ChatFileManager/ChatFileManagerContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useChatData, useChatsData } from "@/data/use-chats-data";
import { useKitData } from "@/data/use-kits-data";
import { useArtifactUuid } from "@/hooks/use-artifact-uuid";
import { useGroupSlug } from "@/hooks/use-group-slug";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { useKitUuid } from "@/hooks/use-kit-uuid";
import { saveBlobAs } from "@/lib/file";
import { groupKitInterviewControllerDownloadTranscript } from "@/lib/gen-brain";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faDownload,
  faFileLines,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AssistBodyContent } from "../../components/Assist/AssistBodyContent";
import { AssistBodyFooter } from "../../components/Assist/AssistBodyFooter";

/**
 * Page for /w/:groupSlug/k/:kitUuid/chat
 *
 * URLs:
 * - /w/:groupSlug/k/:kitUuid/chat - new chat or redirect to last chat
 * - /w/:groupSlug/k/:kitUuid/chat?new=true - new chat
 * - /w/:groupSlug/k/:kitUuid/chat/:chatUuid - specific chat
 */
export const ConversationPage = () => {
  const groupSlug = useGroupSlug();
  const groupUuid = useGroupUuid();
  const kitUuid = useKitUuid();
  const chatUuid = useArtifactUuid("chatUuid");
  const { kit } = useKitData(groupUuid, kitUuid);
  const {
    chats,
    isLoading: isLoadingChats,
    updateChat,
  } = useChatsData(groupUuid, kitUuid);
  const { chat, isLoading: isLoadingChat } = useChatData(
    groupUuid,
    kitUuid,
    chatUuid,
  );
  const { activeChat, activeChatUuid, setActiveChatUuid, forwardChat } =
    useAssistContext();
  const navigate = useNavigate();
  const [openDeleteChatDialog, setOpenDeleteChatDialog] = useState(false);
  const [isDownloadingTranscript, setIsDownloadingTranscript] = useState(false);
  const location = useLocation();
  const creatingUrl =
    (location.search as Record<string, string | undefined>).new === "true";

  useEffect(() => {
    if (creatingUrl) {
      setActiveChatUuid(null);
      return;
    }

    if (isLoadingChats || isLoadingChat) return;

    // If chat is found, proceed with it.
    if (chat) {
      setActiveChatUuid(chat.uuid);
      return;
    }

    // If no chat is found, create new one.
    if (chats.length === 0) {
      navigate({
        to: "/w/$groupSlug/k/$kitUuid/chat",
        params: { groupSlug, kitUuid: kitUuid! },
        search: { new: "true" },
        replace: true,
      });
      return;
    }

    // Redirect to last chat.
    const lastChat = chats[0];
    navigate({
      to: "/w/$groupSlug/k/$kitUuid/chat/$chatUuid",
      params: { groupSlug, kitUuid: kitUuid!, chatUuid: lastChat.uuid },
      replace: true,
    });
  }, [isLoadingChat, isLoadingChats, chat, chats, chatUuid]);

  const handleNewChatClick = () => {
    navigate({
      to: "/w/$groupSlug/k/$kitUuid/chat",
      params: { groupSlug, kitUuid: kitUuid! },
      search: { new: "true" },
      replace: true,
    });
  };

  const handleNewChat = (uuid: string) => {
    navigate({
      to: "/w/$groupSlug/k/$kitUuid/chat/$chatUuid",
      params: { groupSlug, kitUuid: kitUuid!, chatUuid: uuid },
      replace: chat === null,
    });
  };

  const handleDeletion = () => {
    navigate({
      to: "/w/$groupSlug/k/$kitUuid/chat",
      params: { groupSlug, kitUuid: kitUuid! },
      replace: true,
    });
  };

  const handleDownloadTranscript = async () => {
    if (!groupUuid || !kitUuid) return;

    setIsDownloadingTranscript(true);
    try {
      const response = await groupKitInterviewControllerDownloadTranscript({
        path: {
          groupUuid,
          kitUuid,
        },
        parseAs: "blob",
      });

      if (response.data) {
        const blob = response.data as unknown as Blob;
        saveBlobAs(blob, `${kit?.name || "interview"}-transcript.zip`);
        toast.success("Transcript downloaded!");
      }
    } catch (error) {
      console.error("Failed to download transcript:", error);
      toast.error("Failed to download transcript. Please try again.");
    } finally {
      setIsDownloadingTranscript(false);
    }
  };

  function handleUpdateName(name: string) {
    if (!activeChat) return toast.error("Failed to update chat name.");

    updateChat(
      {
        uuid: activeChat.uuid,
        name,
      },
      {
        onError: () => {
          toast.error("Failed to update chat name.");
        },
        onSuccess: ({ response: { ok } }) => {
          if (ok) {
            toast.success("Chat name updated.");
          } else {
            toast.error("Failed to update chat name.");
          }
        },
      },
    );
  }

  return (
    <>
      {chat && <BrowserPageTitle>{chat.name}</BrowserPageTitle>}
      {chat && (
        <AppTopRightMenu.ContentPortal>
          <div className="flex flex-row items-center gap-2">
            {kit?.type === "interview" && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" disabled={isDownloadingTranscript}>
                    {isDownloadingTranscript ? (
                      <>
                        <Dots />
                        Downloading...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faDownload} />
                        Download
                        <FontAwesomeIcon
                          icon={faChevronDown}
                          className="ml-1"
                        />
                      </>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleDownloadTranscript}>
                    <FontAwesomeIcon icon={faFileLines} className="mr-2" />
                    Download Transcript
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {kit?.type !== "interview" && (
              <AssistChatMenu
                onNewChat={handleNewChatClick}
                onDeleteChat={handleDeletion}
              />
            )}
          </div>
        </AppTopRightMenu.ContentPortal>
      )}
      <Page className="bg-background-1">
        <PageHeader>
          <EditableTitle
            value={
              isLoadingChat
                ? ""
                : chat
                  ? chat.name
                  : forwardChat
                    ? `FWD: ${forwardChat.name}`
                    : "New chat"
            }
            onUpdate={handleUpdateName}
            disabled={!activeChat}
          />
        </PageHeader>
        {!activeChat && (
          <PageBody rigid>
            <ExpertSelectionRow />
          </PageBody>
        )}
        <PageBody rigid outerClassName="py-0" innerClassName="py-0">
          <AssistBodyContent.TabsContainer />
        </PageBody>
        <PageBody innerClassName="grow relative">
          <AssistBodyContent variant="minimal" kitType={kit?.type} />
        </PageBody>
        <PageBody rigid outerClassName="py-0" innerClassName="py-0">
          <AssistBodyContent.PaginationContainer />
        </PageBody>
        <PageBody rigid outerClassName="pt-0" innerClassName="pb-4">
          <ChatFileManagerProvider
            groupUuid={undefined}
            kitUuid={undefined}
            chatUuid={activeChatUuid}
          >
            <AssistBodyFooter variant="minimal" onNewChat={handleNewChat} />
          </ChatFileManagerProvider>
        </PageBody>
      </Page>
      <DeleteChatDialog
        open={openDeleteChatDialog}
        onOpenChange={setOpenDeleteChatDialog}
        onDelete={handleDeletion}
      />
    </>
  );
};
